"use client";

import { ArrowRight, Bookmark, BookmarkCheck } from "lucide-react";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useBibStrings } from "@/components/citation-tool/tool-strings";
import Link, { useLocale } from "@/components/locale-link";
import { Button } from "@/components/ui/button";
import { trackCitationSaved } from "@/lib/analytics";
import {
  addCitation,
  BIBLIOGRAPHY_CHANGE_EVENT,
  BIBLIOGRAPHY_STORAGE_KEY,
  type EditableEntry,
  isCitationSaved,
} from "@/lib/bibliography";
import { localePath } from "@/lib/i18n";

interface SaveCitationButtonProps {
  citation: string;
  entry: EditableEntry;
  style: string;
  lang: string;
  onSaved?: () => void;
  /** Show a "View in Studio" link once saved. Off inside the Studio itself. */
  showStudioLink?: boolean;
}

export function SaveCitationButton({
  citation,
  entry,
  style,
  lang,
  onSaved,
  showStudioLink = true,
}: SaveCitationButtonProps) {
  const bib = useBibStrings();
  const router = useRouter();
  const locale = useLocale();
  const [saved, setSaved] = useState(false);
  const [animating, setAnimating] = useState(false);

  // Re-check when the source or active bibliography changes.
  useEffect(() => {
    const updateSaved = () => {
      setSaved(isCitationSaved(entry));
    };
    const handleStorage = (event: StorageEvent) => {
      if (event.key === BIBLIOGRAPHY_STORAGE_KEY) {
        updateSaved();
      }
    };
    updateSaved();
    window.addEventListener(BIBLIOGRAPHY_CHANGE_EVENT, updateSaved);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener(BIBLIOGRAPHY_CHANGE_EVENT, updateSaved);
      window.removeEventListener("storage", handleStorage);
    };
  }, [entry]);

  function handleSave() {
    if (saved) {
      return;
    }

    addCitation(citation, entry, style, lang);
    setSaved(true);
    setAnimating(true);
    trackCitationSaved(style);

    // The toast is the moment the one-off generator hands over to the Studio.
    toast.success(bib.savedToast, {
      action: showStudioLink
        ? {
            label: bib.studioViewInStudio,
            onClick: () => router.push(localePath(locale, "/bibliography")),
          }
        : undefined,
    });

    // Trigger callback for parent to refresh
    onSaved?.();

    // Reset animation state
    setTimeout(() => setAnimating(false), 600);
  }

  return (
    <>
      <Button
        className={`transition-all duration-300 ${animating ? "scale-105" : ""}`}
        disabled={saved}
        onClick={handleSave}
        size="sm"
        variant={saved ? "secondary" : "outline"}
      >
        {saved ? (
          <>
            <BookmarkCheck className="size-4" />
            {bib.saved}
          </>
        ) : (
          <>
            <Bookmark className="size-4" />
            {bib.save}
          </>
        )}
      </Button>
      {saved && showStudioLink ? (
        <Button asChild size="sm" variant="ghost">
          <Link href="/bibliography">
            {bib.studioViewInStudio}
            <ArrowRight className="size-4" />
          </Link>
        </Button>
      ) : null}
    </>
  );
}
